/**
 * TurnstileWidget — minimal Cloudflare Turnstile renderer.
 *
 * No npm dep — loads the Cloudflare script once per page, then calls
 * `window.turnstile.render` for explicit-render control (required for
 * a CRA-style React SPA where the widget mounts dynamically).
 *
 * Usage:
 *   <TurnstileWidget
 *     siteKey={process.env.REACT_APP_TURNSTILE_SITE_AGENCY}
 *     action="agency_signup"
 *     onVerify={(token) => setTurnstileToken(token)}
 *   />
 *
 * Session 213.63.
 */
import React, { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let _scriptLoaded = null;

function loadScript() {
  if (_scriptLoaded) return _scriptLoaded;
  _scriptLoaded = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return _scriptLoaded;
}

export default function TurnstileWidget({
  siteKey,
  action,
  onVerify,
  onExpire,
  onError,
  theme = 'light',
  size = 'normal', // 'normal' | 'compact' | 'invisible'
}) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          size,
          callback: (token) => onVerify?.(token),
          'expired-callback': () => onExpire?.(),
          'error-callback': (err) => onError?.(err),
          'refresh-expired': 'auto',
        });
      })
      .catch((e) => {
        // Turnstile script couldn't load — surface so the form can show
        // the fail-closed message immediately rather than waiting on the
        // backend 503.
        onError?.(e);
      });

    return () => {
      cancelled = true;
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
      } catch { /* ignore */ }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, action]);

  if (!siteKey) {
    // Site key missing from env — show a polite warning. Almost never
    // happens in prod; guards against silent acceptance during dev when
    // someone forgot to set REACT_APP_TURNSTILE_SITE_*.
    return (
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
        Captcha unavailable — site key not configured.
      </div>
    );
  }

  return <div ref={containerRef} data-testid="turnstile-widget" />;
}
